import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '90影院',
    url: 'https://www.90cinema.com/',
    description: '90影院提供8090影院高清、超清、4K、蓝光画质影视资源，电影电视剧综艺动漫免费在线观看，播放流畅不缓冲，全集持续更新。',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@90cinema.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.90cinema.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.90cinema.com/about' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '90影院是免费的吗？',
        acceptedAnswer: { '@type': 'Answer', text: '是的，90影院所有资源完全免费，无需注册，无需付费，打开即可在线观看。' },
      },
      {
        '@type': 'Question',
        name: '90影院的画质如何？',
        acceptedAnswer: { '@type': 'Answer', text: '90影院提供高清、超清、4K、蓝光等多种画质，用户可以根据自己的网络环境和设备选择合适的画质进行观看。' },
      },
      {
        '@type': 'Question',
        name: '90影院有哪些影视资源？',
        acceptedAnswer: { '@type': 'Answer', text: '90影院涵盖电影、电视剧、综艺、动漫等多种类型，资源丰富，每日持续更新。' },
      },
      {
        '@type': 'Question',
        name: '如何联系90影院？',
        acceptedAnswer: { '@type': 'Answer', text: '您可以通过电子邮箱 service@90cinema.com 与我们取得联系，我们会在24小时内回复您的邮件。' },
      },
    ],
  };

  const stats = [
    { value: '10,000+', label: '影视资源', icon: 'ri-movie-2-line' },
    { value: '50万+', label: '日均访问', icon: 'ri-user-line' },
    { value: '99.9%', label: '播放流畅率', icon: 'ri-speed-line' },
    { value: '4K蓝光', label: '最高画质', icon: 'ri-4k-line' },
  ];

  const features = [
    {
      title: '高清画质',
      desc: '提供高清、超清、4K、蓝光等多种画质选择，让您享受影院级观影体验。',
      icon: 'ri-hd-line',
    },
    {
      title: '流畅播放',
      desc: '采用CDN加速和智能缓冲技术，确保播放不卡顿，告别缓冲等待。',
      icon: 'ri-speed-up-line',
    },
    {
      title: '海量资源',
      desc: '涵盖电影、电视剧、综艺、动漫等上万部影视作品，每日持续更新。',
      icon: 'ri-database-2-line',
    },
    {
      title: '完全免费',
      desc: '所有影视资源完全免费在线观看，无需注册登录，无需任何费用。',
      icon: 'ri-money-cny-circle-line',
    },
    {
      title: '多端适配',
      desc: '完美适配电脑、平板、手机等多种设备，随时随地畅享影视。',
      icon: 'ri-device-line',
    },
    {
      title: '及时更新',
      desc: '与院线和平台同步更新，第一时间为您呈现最新热门影视内容。',
      icon: 'ri-refresh-line',
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="px-4 md:px-6 py-4 bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-screen-2xl mx-auto flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </div>
        </nav>

        <section className="py-12 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-heading text-foreground-900 mb-4">
              关于90影院
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              90影院是一个专注于提供高品质影视资源的在线观看平台。我们致力于为广大影迷提供高清、超清、4K、蓝光画质的电影、电视剧、综艺、动漫资源，让每一位用户都能享受流畅、高清的观影体验。
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-2xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-full">
                    <i className={`${stat.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900 mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-3">
              品牌介绍
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10 max-w-2xl mx-auto">
              90影院自成立以来，始终坚持以用户体验为核心，不断优化服务质量
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {features.map((feat) => (
                <div key={feat.title} className="p-5 md:p-6 bg-background-100 rounded-lg border border-background-200 hover:border-primary-200 transition-all">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                    <i className={`${feat.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10 max-w-2xl mx-auto">
              来自真实用户的评价与反馈
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                { name: '张先生', text: '90影院的画质真的很不错，4K蓝光看大片太爽了！而且完全不卡顿，比很多付费平台体验还好。', rating: 5 },
                { name: '李女士', text: '我最喜欢看电视剧，90影院的资源更新很快，热门剧基本都能第一时间看到，画质也很清晰。', rating: 5 },
                { name: '王同学', text: '作为一个动漫迷，90影院的动漫资源非常全，海贼王、鬼灭之刃都有，而且是高清资源，非常满意。', rating: 5 },
                { name: '赵先生', text: '每天下班回家打开90影院看看电影，已经成了我的日常。资源丰富，播放流畅，强烈推荐！', rating: 5 },
                { name: '刘女士', text: '综艺爱好者福音！奔跑吧、向往的生活这些热门综艺更新很快，画质清晰，体验很好。', rating: 4 },
                { name: '陈同学', text: '完全免费还能有这么好的画质和流畅度，90影院真的太良心了。已经推荐给很多朋友了。', rating: 5 },
              ].map((review, idx) => (
                <div key={idx} className="p-5 md:p-6 bg-background-50 rounded-lg border border-background-200">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{review.text}&rdquo;</p>
                  <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10 max-w-2xl mx-auto">
              如果您有任何问题或建议，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto">
              <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-600">service@90cinema.com</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-time-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">服务时间</h3>
                <p className="text-sm text-foreground-600">7×24小时全天候服务</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-feedback-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">反馈建议</h3>
                <p className="text-sm text-foreground-600">期待您的宝贵意见</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-6">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                { q: '90影院是免费的吗？', a: '是的，90影院所有资源完全免费，无需注册，无需付费，打开即可在线观看。我们致力于为广大影迷提供优质的免费观影服务。' },
                { q: '90影院的画质如何？', a: '90影院提供高清、超清、4K、蓝光等多种画质，用户可以根据自己的网络环境和设备选择合适的画质进行观看。所有资源均经过精心处理，确保画质清晰。' },
                { q: '90影院有哪些影视资源？', a: '90影院涵盖电影、电视剧、综艺、动漫等多种类型，资源丰富，每日持续更新。无论是热门大片、经典老片还是最新热播剧，都能在90影院找到。' },
                { q: '播放为什么这么流畅？', a: '90影院采用先进的CDN内容分发网络和智能缓冲技术，确保视频播放流畅不卡顿。即使在高清甚至4K画质下，也能获得流畅的观看体验。' },
                { q: '如何联系90影院？', a: '您可以通过电子邮箱 service@90cinema.com 与我们取得联系，我们会在24小时内回复您的邮件。您的每一条反馈对我们都很重要。' },
              ].map((faq, idx) => (
                <details key={idx} className="group border border-background-200 rounded-lg bg-background-50">
                  <summary className="flex items-center justify-between px-4 md:px-6 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="px-4 md:px-6 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}